<template lang="pug">
#room-multiple-register(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 部屋情報一括登録
      .buttons
        button.btn(@click="onExampleModal" type="button") CSVファイルの仕様と作成例
        button.btn.template-btn(@click="onTemplateDownload" type="button") CSVファイルの雛形ダウンロード
    
    .box-body.register-body
      .note
        span 複数の部屋情報をCSVファイルで作成すると、一括登録することが出来ます。
        br
        span CSVファイルの仕様と作成例は、右上の「CSVファイルの仕様と作成例」をご確認ください。
        br
        span 新しくCSVファイルを作成する場合は、右上の「CSVファイルの雛形ダウンロード」をご利用ください。
      // inputs
      FormInputSelect(
        key-name="文字コード"
        name="encoding"
        v-model="encoding"
        :options="encodingOptions"
        :validation-rules="{ }"
        :styles="{ 'max-width': '540px' }")
      FormFile(
        key-name="CSVファイルの読み込み"
        name="csv-file"
        v-model="csv"
        :showDragAndDrop="false"
        :file-name.sync="fileName"
        mimetype=".csv"
        :validationRules="{}"
        height="32px"
        @input="inputCsv")
      
      ValidationAlert(v-model="0 < csvErrorMessages.length" :errorMessages="csvErrorMessages")

      // table
      .preview-table-content
        RoomCsvTable(
          v-show="0 < items.length"
          v-model="items"
          :fields="fields"
          :isLoading="isLoading"
        )

    .box-footer.with-border.register-footer
      .buttons
        button.btn.register-btn(v-if="csvErrorMessages.length == 0 && 0 < items.length" @click="showConfirmModal" type="button") 登録
        button.btn.register-btn.disable(v-else type="button" disabled) 登録
        button.btn.cancel-btn(@click="gotoRoomList" type="button") キャンセル

  OkCancelModal(v-model="showRegisterModal" title="部屋情報を登録しますか？" @ok="registerRooms")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoRoomList")
  RoomMultipleRegisterExampleModal(v-model="showExampleModal")
  
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { SendWaiterMessagesState } from "@/store/NeedRefreshes";
import templateCsv from "@/assets/template/room-register-template.csv";

import { PcUrl } from "@/commons/constants/PcUrl";
import FileReaderWrapper from "@/commons/utils/FileReaderWrapper";
import RoomsService, { RoomGetModel, RoomPostModel, RoomType, UseType } from "@/commons/services/Rooms";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import RoomMultipleRegisterExampleModal from "@/pc/components/modals/RoomMultipleRegisterExampleModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByStrings.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import FormFile from "@/pc/components/forms/FormFile.vue";
import RoomCsvTable, { RoomCsvTableField, RoomCsvTableItem } from "@/pc/containers/settings/RoomCsvTable.vue";
import SystemError from "@/commons/models/SystemError";

/**
 * 部屋登録画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    RoomMultipleRegisterExampleModal,
    PageLoading,
    ValidationAlert,
    FormInputSelect,
    FormFile,
    RoomCsvTable
  }
})
export default class ResidentMultipleRegister extends Vue {
  /** 部屋サービス */
  @Inject("RoomsService") roomsService!: RoomsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 記載例モーダル表示可否 */
  showExampleModal: boolean = false;
  /** 登録確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** 部屋IDと部屋名のMAP */
  roomNameMap: Map<string, number> = new Map();
  /** グループIDとグループ名のMAP */
  groupNameMap: Map<string, number> = new Map();

  /** 文字エンコーディング */
  encoding: string = "";
  /** 文字エンコーディング選択肢 */
  encodingOptions: Array<{ value: number | string; label: string }> = [];
  /** CSVファイル */
  csv: File | null = null;
  /** 表示用ファイル名 */
  fileName: string = "";

  /** プレビューテーブルの列名 */
  fields: Array<RoomCsvTableField> = [];
  /** プレビューテーブルの各行 */
  items: Array<RoomCsvTableItem> = [];
  /** プレビューテーブル読込み中か否か */
  isLoading: boolean = false;

  /** エラーメッセージ */
  csvErrorMessages: string[] = [];

  /**
   * ### 文字エンコードの変更イベント
   *
   * - 現在読込んでいるCSVで再度CSV読込み処理を行わせる
   */
  @Watch("encoding")
  public changeEncoding(): void {
    this.inputCsv(this.csv);
    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   *
   * - プレビュー用のテーブルの列名を作る
   * - 部屋IDと部屋名の変換用に部屋情報のMAPを作る
   * - 文字エンコードの選択肢を作る
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const fields: Array<RoomCsvTableField> = this.createFields();
    this.fields = fields;

    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const groupNameMap: Map<string, number> = this.createGroupNameMap(groups);
    this.groupNameMap = groupNameMap;

    const rooms: Array<RoomGetModel> = await this.getRooms();
    const roomNameMap: Map<string, number> = this.createRoomNameMap(rooms);
    this.roomNameMap = roomNameMap;

    this.encodingOptions = [
      { value: "", label: "自動判別" },
      { value: "SHIFT_JIS", label: "SJIS（Shift-JIS）" },
      { value: "UTF-8", label: "UTF-8" }
    ];

    this.isBusy = false;

    return;
  }

  /**
   * ### プレビューテーブルの列名を作成する
   */
  public createFields(): Array<RoomCsvTableField> {
    let fields: Array<RoomCsvTableField> = [];

    fields.push({ key: "rowNo", label: "行番号", sortable: false, sortDirection: "desc", class: "row-no" });
    fields.push({ key: "residentGroupName", label: "利用者グループ", sortable: false, sortDirection: "desc", class: "resident-group-name" });
    fields.push({ key: "name", label: "部屋名", sortable: false, sortDirection: "desc", class: "room-name" });
    fields.push({ key: "useType", label: "部屋用途", sortable: false, sortDirection: "desc", class: "use-type" });
    fields.push({ key: "roomType", label: "部屋種別", sortable: false, sortDirection: "desc", class: "room-type" });
    fields.push({ key: "capacity", label: "部屋定員", sortable: false, sortDirection: "desc", class: "capacity" });

    return fields;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### グループIDとグループ名のMAPを作成する
   */
  public createGroupNameMap(residentGroups: Array<ResidentGroupsGetModel>): Map<string, number> {
    let map: Map<string, number> = new Map<string, number>();
    for (const group of residentGroups) {
      map.set(group.name, group.id);
    }

    return map;
  }

  /**
   * ### 部屋を取得する
   */
  public async getRooms(): Promise<Array<RoomGetModel>> {
    const response: Array<RoomGetModel> = await this.roomsService.gets(userStore);
    return response;
  }

  /**
   * ### 部屋IDと部屋名のMAPを作成する
   */
  public createRoomNameMap(rooms: Array<RoomGetModel>): Map<string, number> {
    let roomMap: Map<string, number> = new Map<string, number>();
    for (const room of rooms) {
      roomMap.set(room.name, room.id);
    }

    return roomMap;
  }

  /**
   * ### CSV読込み処理
   *
   * - プレビューテーブルの項目、CSVエラーメッセージを初期化する
   * - ファイルを読込んでテキスト情報とする
   * - テキスト情報の文字コードはUnicodeに変換する
   * - 変換できない(予期せぬ文字コード)の場合は読込めないCSVファイルとしてエラーメッセージを表示し、処理を中断する
   * - テキスト情報は改行コードで配列にし、行単位に扱う
   * - CSVの仕様により最終行には空行が存在する可能性があるのでその場合は削除する
   * - 1行目
   *   - 先頭列にSERVICE_USER_INFOという種別認識用の文字列が無ければエラーとし、処理を中断する
   * - 2行目
   *   - 項目を指す行なので項目名情報を作る
   *   - 項目必須チェック、項目重複チェックを行いエラーがあれば処理を中断する
   * - 3行目以降
   *   - プレビューテーブル表示用の情報を作る
   *   - 項目ごとにエラーチェックを行うが、エラーがあっても処理は中断しない
   */
  public async inputCsv(file: File | null): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "CSVファイル読込み中...";
    this.isLoading = true;

    this.items = [];
    this.csvErrorMessages = [];

    if (file != null) {
      // CSVを読込んで文字列を取得する
      const allowdEncoding: string[] = ["UTF-8", "SHIFT_JIS"];
      const encoding: string | undefined = this.encoding ? this.encoding : undefined;
      const encodedText: { unicodeText: string; sourceEncoding: Encoding.Encoding } | null = await FileReaderWrapper.getUnicodeText(file, allowdEncoding, encoding); // eslint-disable-line

      // エンコードできなかった場合はエラー
      if (encodedText == null) {
        this.csvErrorMessages.push("読込み出来ないCSVファイルです");
        this.isBusy = false;
        return;
      }

      // 変換対象が許可していない文字コードの場合はsourceEncodingがnullなのでエラー
      if (encodedText.sourceEncoding == null) {
        this.csvErrorMessages.push("対応外の文字コードです。SJISで旧字体などを使用している場合は、文字コードを自動判別ではなくSJISにして再度読込ませてください。");
        this.isBusy = false;
        return;
      }

      // CSVを改行コードで行として分割する
      let lines: string[] = encodedText.unicodeText.split(/\r\n|\n|\r/g);

      // CSVの仕様で最後のレコードの後には改行はあってもなくてもいいので、最終行が空行の場合は削除する
      const lastLine: string = lines[lines.length - 1];
      if (!lastLine) {
        lines.pop();
      }

      console.debug("lines:", lines);

      // CSVの形式は1行目：固定文字列、2行目：列名、3行目移行：部屋の情報なので3行未満ならエラー
      if (lines.length < 3) {
        this.csvErrorMessages.push("CSVファイルの形式が不正です。最低でも3行以上必要です");
        this.isBusy = false;
        return;
      }

      // 1行目の先頭に記載されている種別文字列チェック
      const firstLineRows: string[] = lines[0].split(",");

      console.debug(firstLineRows[0], "SERVICE_ROOM_INFO");

      if (String(firstLineRows[0]) != "SERVICE_ROOM_INFO") {
        this.csvErrorMessages.push("1行目はSERVICE_ROOM_INFOの記載が必要です");
        this.isBusy = false;
        return;
      }

      // 2行目のチェックと列番地と列名のMAPを作成する
      const rowAddressNameMap: Map<number, string> = this.createCsvRowAddressNameMap(lines[1]);
      if (0 < this.csvErrorMessages.length) {
        this.isBusy = false;
        return;
      }

      // 3行目以降の情報からテーブル用の情報を作るので1行目と2行目を削ったものを使用する
      let dataLines: string[] = lines.slice();
      dataLines.splice(0, 2);
      // 各行で項目数(カンマの数)の整合性チェックが必要なので2行目の項目数を作る
      const rowNameCount: number = lines[1].split(",").length;
      const items: Array<RoomCsvTableItem> = this.createItems(dataLines, rowAddressNameMap, this.roomNameMap, this.groupNameMap, rowNameCount);

      this.items = items;
    }

    this.isLoading = false;
    this.isBusy = false;

    return;
  }

  /**
   * ### CSVの列番地と列名のMAPを作成する
   *
   * - 「,」区切りで配列化
   * - 配列から列名の番地のMAPを作る
   * - 項目名の重複チェック
   * - 項目名の必須チェック
   */
  public createCsvRowAddressNameMap(line: string): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();

    let existName: boolean = false;
    let existUseType: boolean = false;
    let existRoomType: boolean = false;
    let existCapacity: boolean = false;
    let existResidentGroup: boolean = false;

    const rows: string[] = line.split(",");
    for (let i = 0; i < rows.length; i++) {
      map.set(i, rows[i]);

      // 項目名の重複チェック
      switch (rows[i]) {
        case "name": {
          if (existName == true) {
            this.csvErrorMessages.push("2行目にnameが重複しています。nameの列は1列だけにしてください");
          } else {
            existName = true;
          }
          break;
        }
        case "use_type": {
          if (existUseType == true) {
            this.csvErrorMessages.push("2行目にuse_typeが重複しています。use_typeの列は1列だけにしてください");
          } else {
            existUseType = true;
          }
          break;
        }
        case "room_type": {
          if (existRoomType == true) {
            this.csvErrorMessages.push("2行目にroom_typeが重複しています。room_typeの列は1列だけにしてください");
          } else {
            existRoomType = true;
          }
          break;
        }
        case "capacity": {
          if (existCapacity == true) {
            this.csvErrorMessages.push("2行目にcapacityが重複しています。capacityの列は1列だけにしてください");
          } else {
            existCapacity = true;
          }
          break;
        }
        case "group_name": {
          if (existResidentGroup == true) {
            this.csvErrorMessages.push("2行目にgroup_nameが重複しています。group_nameの列は1列だけにしてください");
          } else {
            existResidentGroup = true;
          }
          break;
        }
        default: {
          break;
        }
      }
    }

    // 項目名の必須チェック
    if (existName == false) {
      this.csvErrorMessages.push("2行目にnameの列は必須です");
    }
    if (existUseType == false) {
      this.csvErrorMessages.push("2行目にuse_typeの列は必須です");
    }
    if (existRoomType == false) {
      this.csvErrorMessages.push("2行目にroom_typeの列は必須です");
    }

    return map;
  }

  /**
   * ### プレビューテーブル表示用のデータを作成する
   *
   * - 各行をカンマ区切りで配列に分ける
   * - CSV2行目の項目数とデータの配列数が合わない場合はエラーとする
   * - 配列の各項目が何の項目かをMAPから取得し、項目に応じて表示用データの項目に割り当てる
   * - 各項目毎にエラーチェックし、エラーがあれば表示用エラーメッセージに追加する
   * - エラーが発生しても処理は中断せず、全エラーを表示させる
   * - 部屋名については分割して保持しておき、その行のデータを全部読み込み終わった後必要に応じて結合して部屋名とする
   */
  public createItems(
    lines: string[],
    rowAddressNameMap: Map<number, string>,
    roompNameMap: Map<string, number>,
    groupNameMap: Map<string, number>,
    rowNameCount: number
  ): Array<RoomCsvTableItem> {
    let items: Array<RoomCsvTableItem> = [];
    let registerRoomNameMap: Map<string, number> = new Map<string, number>();

    for (let i = 0; i < lines.length; i++) {
      let item: RoomCsvTableItem = {
        rowNo: i + 3,
        name: "",
        useType: UseType.UNKNOWN,
        useTypeName: "",
        roomType: RoomType.UNKNOWN,
        roomTypeName: "",
        capacity: null,
        residentGroupId: 1,
        residentGroupName: "無所属"
      };
      let tempCapacity: string = "";

      // 項目数の整合性チェック
      const rows: string[] = lines[i].split(",");
      if (rows.length != rowNameCount) {
        this.csvErrorMessages.push(`${i + 3}行目は不正なデータ行です。${i + 3}行目の項目数が2行目の項目数と異なります`);
        item.name = undefined;
        item.useType = undefined;
        item.useTypeName = undefined;
        item.roomType = undefined;
        item.roomTypeName = undefined;
        item.capacity = undefined;
        item.residentGroupId = undefined;
        item.residentGroupName = undefined;

        continue;
      }

      for (let j = 0; j < rows.length; j++) {
        const rowName: string | undefined = rowAddressNameMap.get(j);
        switch (rowName) {
          case "name": {
            // 必須チェック
            if (rows[j] == "") {
              this.csvErrorMessages.push(`${i + 3}行目のnameは必須です`);
              item.name = undefined;
              break;
            }
            let isError: boolean = false;
            // 既に同じ部屋名が登録されていたらエラー
            const roomId: number | undefined = roompNameMap.get(rows[j]);
            if (roomId != undefined) {
              this.csvErrorMessages.push(`${i + 3}行目のnameと同一の部屋名が既に存在しているため登録できません`);
              isError = true;
            }
            // 登録対象に同じ部屋名が存在したらエラー
            const rowNo: number | undefined = registerRoomNameMap.get(rows[j]);
            if (rowNo != undefined) {
              this.csvErrorMessages.push(`${rowNo}行目のnameと${i + 3}行目のnameが同一の部屋名のため登録できません`);
              isError = true;
            } else {
              registerRoomNameMap.set(rows[j], i + 3);
            }

            if (isError) {
              item.name = undefined;
              break;
            }

            item.name = rows[j];
            break;
          }
          case "use_type": {
            // 必須チェック
            if (rows[j] == "") {
              this.csvErrorMessages.push(`${i + 3}行目のuse_typeは必須です`);
              item.useType = undefined;
              item.useTypeName = undefined;
              break;
            }
            let isError: boolean = false;
            // 全角文字以外が含まれていたらエラー
            const useTypeName: string = rows[j];
            if (!this.isFullWidthCharacter(useTypeName)) {
              this.csvErrorMessages.push(`${i + 3}行目のuse_typeに全角文字以外が含まれています。全角文字で入力してください`);
              isError = true;
            }
            // コード値に変換して変換できなければエラー
            const useType: UseType = this.convertUseType(useTypeName);
            if (useType == UseType.UNKNOWN) {
              this.csvErrorMessages.push(`${i + 3}行目のuse_typeが有効な値ではありません。空床管理対象外、入居用部屋、ショートステイのいずれかを入力してください`);
              isError = true;
            }
            if (isError) {
              item.useType = undefined;
              item.useTypeName = undefined;
              break;
            }

            item.useType = useType;
            item.useTypeName = useTypeName;
            break;
          }
          case "room_type": {
            // 必須チェック
            if (rows[j] == "") {
              this.csvErrorMessages.push(`${i + 3}行目のroom_typeは必須です`);
              item.roomType = undefined;
              item.roomTypeName = undefined;
              break;
            }
            let isError: boolean = false;
            // 全角文字以外が含まれていたらエラー
            const roomTypeName: string = rows[j];
            if (!this.isFullWidthCharacter(roomTypeName)) {
              this.csvErrorMessages.push(`${i + 3}行目のroom_typeに全角文字以外が含まれています。全角文字で入力してください`);
              isError = true;
            }
            // コード値に変換して変換できなければエラー
            const roomType: RoomType = this.convertRoomType(roomTypeName);
            if (roomType == RoomType.UNKNOWN) {
              this.csvErrorMessages.push(
                `${i + 3}行目のroom_typeが有効な値ではありません。空床管理対象外、個室、２人部屋（夫婦部屋）、多床室（男性）、多床室（女性）のいずれかを入力してください`
              );
              isError = true;
            }
            if (isError) {
              item.roomType = undefined;
              item.roomTypeName = undefined;
              break;
            }

            item.roomType = roomType;
            item.roomTypeName = roomTypeName;
            break;
          }
          case "capacity": {
            tempCapacity = rows[j];
            break;
          }
          case "group_name": {
            // 空だったら無所属扱い
            if (rows[j] == "") {
              item.residentGroupId = 1;
              item.residentGroupName = "無所属";
              break;
            }
            // グループ名からグループIDが逆引き出来なかったらエラーとして扱う
            const groupId: number | undefined = groupNameMap.get(rows[j]);
            if (groupId == undefined) {
              this.csvErrorMessages.push(`${i + 3}行目のgroup_nameが存在しません。存在するグループ名を入力してください`);
              item.residentGroupId = undefined;
              item.residentGroupName = undefined;
              break;
            }

            item.residentGroupId = groupId;
            item.residentGroupName = rows[j];
            break;
          }
        }
      }

      // 人数の扱いは部屋種別によって決まる
      switch (item.roomType) {
        // 部屋種別が多床室以外の場合は人数は自動入力する
        case RoomType.UNMANAGED: {
          item.capacity = 0;
          break;
        }
        case RoomType.SINGLE: {
          item.capacity = 1;
          break;
        }
        case RoomType.DOUBLE: {
          item.capacity = 2;
          break;
        }
        // 部屋種別が多床室の場合は人数は入力値を使用するのでチェックする
        case RoomType.MULTI_MEN:
        case RoomType.MULTI_WOMEN: {
          // 必須チェック
          if (tempCapacity == "") {
            this.csvErrorMessages.push(`${i + 3}行目の部屋用途が多床室のため、capacityは必須です`);
            item.capacity = undefined;
            break;
          }
          // 数値チェック
          let isError: boolean = false;
          const capacity: number = Number(tempCapacity);
          if (isNaN(capacity)) {
            this.csvErrorMessages.push(`${i + 3}行目のcapacityは数値にしてください`);
            isError = true;
          }
          // 桁数チェック
          if (3 < tempCapacity.length) {
            this.csvErrorMessages.push(`${i + 3}行目のcapacityは3桁までの数値にしてください`);
            isError = true;
          }

          if (isError) {
            item.capacity = undefined;
          } else {
            item.capacity = capacity;
          }
          break;
        }
        default: {
          item.capacity = undefined;
          break;
        }
      }

      items.push(item);
    }

    return items;
  }

  /**
   * ### 全角文字のみかを返す
   *
   * - 全角文字のみならtrue、全角文字以外が含まれていたらfalse
   */
  public isFullWidthCharacter(str: string): boolean {
    const regex = new RegExp("^[^\x01-\x7E\xA1-\xDF]+$");
    if (str.match(regex)) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### 用途名から用途コード値を返す
   */
  public convertUseType(useTypename: string): UseType {
    switch (useTypename) {
      case "空床管理対象外": {
        return UseType.UNMANAGED;
      }
      case "入居用部屋": {
        return UseType.NORMAL_STAY;
      }
      case "ショートステイ": {
        return UseType.SHORT_STAY;
      }
      default: {
        return UseType.UNKNOWN;
      }
    }
  }

  /**
   * ### 部屋種別名から部屋種別コード値を返す
   */
  public convertRoomType(roomTypeName: string): RoomType {
    switch (roomTypeName) {
      case "空床管理対象外": {
        return RoomType.UNMANAGED;
      }
      case "個室": {
        return RoomType.SINGLE;
      }
      case "２人部屋（夫婦部屋）": {
        return RoomType.DOUBLE;
      }
      case "多床室（男性）": {
        return RoomType.MULTI_MEN;
      }
      case "多床室（女性）": {
        return RoomType.MULTI_WOMEN;
      }
      default: {
        return RoomType.UNKNOWN;
      }
    }
  }

  /**
   * ### 登録確認モーダルを表示する
   */
  public async showConfirmModal(): Promise<void> {
    this.showRegisterModal = true;
    return;
  }

  /**
   * ### 部屋を登録する
   *
   * - Chromeのリクエスト数に上限があるので50人ずつ単位で並列でリクエストを送る
   * - 登録完了モーダルを表示する
   */
  public async registerRooms(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    for (let i = 0; i * 50 < this.items.length; i++) {
      const slicedItems: Array<RoomCsvTableItem> = this.items.slice(i * 50, i * 50 + 50);
      let promises: Array<Promise<void>> = [];

      for (const item of slicedItems) {
        const promise: Promise<void> = this.createRegisterPromise(item);
        promises.push(promise);
      }

      await Promise.all(promises).then((results: Array<any>) => {
        for (const result of results) {
          if (result instanceof SystemError) {
            console.error("error:", result);
          }
        }
      });

      // メッセージ送信(待機者)のテンプレートが変更になるので再読み込みさせるようstoreを更新
      let sendWaiterMessageState: SendWaiterMessagesState = needRefreshesStore.sendWaiterMessage;
      sendWaiterMessageState.sendMessages = true;
      needRefreshesStore.setSendWaiterMessage(sendWaiterMessageState);
    }

    this.isBusy = false;

    // 登録成功ダイアログを表示する
    this.showRegisterSuccessModal = true;

    return;
  }

  /**
   * ### 部屋登録Promiseを作成する
   */
  public async createRegisterPromise(item: RoomCsvTableItem): Promise<void> {
    return new Promise(async (resolve: any) => {
      try {
        const requestBody: RoomPostModel = this.createRequestBody(item);
        await this.roomsService.post(userStore, requestBody);
        resolve();
      } catch (error) {
        // Promise.all()で並列に実行されるので例外を投げなおすのではなく例外を戻り値として返す
        resolve(error);
      }
    });
  }

  /**
   * ### 部屋登録パラメータを作成する
   */
  public createRequestBody(item: RoomCsvTableItem): RoomPostModel {
    const requestBody: RoomPostModel = {
      name: item.name as string,
      useType: item.useType as UseType,
      roomType: item.roomType as RoomType,
      capacity: item.capacity as number,
      residentGroupId: item.residentGroupId as number
    };

    return requestBody;
  }

  /**
   * ### 部屋一覧に遷移する
   */
  public gotoRoomList(): void {
    this.$router.push(PcUrl.RoomList);
    return;
  }

  /**
   * ### 使用例モーダルを表示する
   */
  public onExampleModal(): void {
    this.showExampleModal = true;
    return;
  }

  /**
   * ### テンプレートのダウンロード
   *
   * - 本来ならHTML5のa(href="csvのパス" download)でダウンロードさせたいが、vueだとファイルが存在しないになってしまう
   * - 解決策としてassetsにあるテンプレートのCSVを読込んでBlobに変換する
   * - aタグを内部的に作って、変換したBlobをダウンロードさせる
   */
  public onTemplateDownload(): void {
    let blob = new Blob([templateCsv], { type: "text/csv" });
    let link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "room-register-template.csv";
    link.click();

    return;
  }
}
</script>

<style lang="scss" scoped>
#room-multiple-register {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .error-message-content {
    width: 100%;
    max-height: 300px;
    overflow-y: auto;
    margin: 0 20px;
  }

  .note {
    margin: 0 0 10px 0;
  }

  .preview-table-content {
    max-width: calc(100% - 30px);
    margin: 0 20px;
  }

  .template-btn {
    margin-left: 12px;
  }

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
